import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import delay from 'lodash/delay';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import size from 'lodash/size';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FilterSettings, SortFull, SortHalf } from '@yme/assets/icons';
import { BottomSheet, Header } from '@yme/atoms/bottom-sheet';
import { Button, CONTAINED_VARIANT, NO_ICON, SIZE_44 } from '@yme/atoms/button';
import { Dropdown } from '@yme/atoms/dropdown';
import { ClearAll } from '@yme/atoms/filters-elements';
import { Col, Container, Row } from '@yme/atoms/grid';
import { Pills } from '@yme/atoms/pills';
import { SpinnerLoader } from '@yme/atoms/spinner-loader';
import { AEM_FUNCTIONS } from '@yme/common/aem-functions';
import { FilterType } from '@yme/common/constants';
import { useWindowSize } from '@yme/hooks/index';
import { CheckboxesDropdown, FilterDropdown } from '@yme/molecules/filter-dropdown';
import { SectionWrapper } from '@yme/molecules/section-wrapper';
import { Toggle as ToggleComponent } from '@yme/molecules/toggle/Toggle';
import { getCurrencyRate } from '@yme/selectors/common';
import { getDaysToShowNew, getRTL } from '@yme/selectors/index';
import { useTranslation } from '@yme/services/i18n';
import { colorCoreWhite1000 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { HeadingLevel3, RegularTextLevel2, RegularTextLevel3 } from '@yme/tokens/typography';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { getPlaceholderLabelKey } from '../ComparisonFilter/helpers';
import { getCountAppliedFilters, getCountAppliedValues, getDefaultPerPageLimit, getOptionsForFilter, } from '../common/helpers';
import { FilterResults, DidYouMeanForGrid } from './components';
import { FiltersButtonStyled, FiltersListStyled, FilterWrapperStyled, FlexWrapperStyled, ResultItemsCounterStyled, RowStyled, SearchInputStyled, SortOrderStyled, SpinnerLoaderContainerStyled, } from './styled';
import { useProductFilter } from './useProductFilter';
import { useScrollRestoration } from './useScrollRestoration';
import { LOADED_ITEMS_OFFSET, MAX_SEARCH_SYMBOLS, MIN_SEARCH_SYMBOLS } from './';
export function AccessoriesFilterBloomreach(props) {
    const { cqType, filterSettings: { sortOptionsType, perPageLimitSetting }, categoryIds, categoryId = '', pdpPageUrl, filterFacetsConfig, productEndpoint, compatiblePanelAPI, isDiscountMode, productType, isInEditor, accessoryTypeTranslations = [], accessoryUsageTranslations, subcategoryTranslations, genderTranslations, } = props;
    const isRightToLeft = useSelector(getRTL);
    const daysToShowNew = useSelector(getDaysToShowNew);
    const currencyRate = useSelector(getCurrencyRate);
    const isTracked = useRef(false);
    const containerId = 'productsGrid';
    const [isFiltersBottomSheetOpened, setIsFiltersBottomSheetOpened] = useState(false);
    const { isMobileView, isTabletView, isInfiniteDesktopView } = useWindowSize();
    const { t, dictionary } = useTranslation();
    const isCMPPage = cqType === AEM_FUNCTIONS.plpClothingMerchGrid;
    const { pathname } = useLocation();
    const pathnameSplitArray = pathname.split('/');
    const accessoryCategory = last(pathnameSplitArray?.filter((item) => Boolean(item))) || '';
    const lioFromSessionStorage = sessionStorage.getItem(LOADED_ITEMS_OFFSET);
    const lio = lioFromSessionStorage
        ? JSON.parse(lioFromSessionStorage)
        : {};
    const loadedItemsOffset = +(lio[accessoryCategory] ?? 0);
    const { data, autoCorrectQueryResult, didYouMeanOptions, isDataExists, isLoaded, isLoading, hasDiscountedProducts, discountedApi, 
    // Sort Order API
    sortingProps, 
    // Cleared filter API
    filterClearedProps, 
    // Counter API
    total, 
    // Search API
    searchInput, 
    // Pills API
    pills, 
    // facets options
    facetsProps, fetchProducts, offset, setOffset, isFacetsLoaded, } = useProductFilter({
        categoryIds,
        categoryId,
        filterFacetsConfig,
        sortOptionsType,
        compatiblePanelAPI,
        isDiscountMode,
        isCMPPage,
        productType,
        loadedItemsOffset,
        accessoryTypeTranslations,
        accessoryUsageTranslations,
        genderTranslations,
        subcategoryTranslations,
    });
    const isAllProductsLoaded = size(data) === total;
    const { updateItemsRef } = useScrollRestoration({
        offset,
        isLoading,
        accessoryCategory,
        lio,
    });
    const isLoadedItemsOffsetIncluded = () => lio[accessoryCategory] || lio[accessoryCategory] === '0';
    const fetchDataWithOffset = async () => {
        await fetchProducts(0, offset + getDefaultPerPageLimit(window.innerWidth, perPageLimitSetting));
        setOffset(offset);
    };
    const fetchData = async () => {
        if (!isLoadedItemsOffsetIncluded()) {
            await fetchProducts(0, getDefaultPerPageLimit(window.innerWidth, perPageLimitSetting));
        }
        else {
            await fetchDataWithOffset();
        }
    };
    useEffect(() => {
        if (isFacetsLoaded) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFacetsLoaded, fetchProducts]);
    const fetchMoreProducts = useCallback(async () => {
        if (!isAllProductsLoaded) {
            const defaultPerPageLimit = getDefaultPerPageLimit(window.innerWidth, perPageLimitSetting);
            await fetchProducts(offset + defaultPerPageLimit, defaultPerPageLimit);
        }
    }, [fetchProducts, isAllProductsLoaded, offset, perPageLimitSetting]);
    const countDiscountedFilter = isDiscountMode ? 0 : Number(discountedApi.isShowOnlyDiscounted);
    const countAppliedFilters = getCountAppliedFilters(facetsProps.facets) + countDiscountedFilter;
    const filtersLabel = countAppliedFilters
        ? `${t('filters_filters_label')} (${countAppliedFilters})`
        : t('filters_filters_label');
    const sortOrderOption = useMemo(() => {
        const currentOption = sortingProps.sortFilterOptions.find((option) => option.value === sortingProps.sortOrder);
        const defaultOption = sortingProps.sortFilterOptions.find((option) => Boolean(option?.isValueDefault)) ||
            sortingProps.sortFilterOptions[0];
        return currentOption ?? defaultOption;
    }, [sortingProps.sortFilterOptions, sortingProps.sortOrder]);
    const facets = useMemo(() => {
        return facetsProps?.facets.length
            ? facetsProps.facets
            : filterFacetsConfig.map((filterFacet) => {
                const itemResult = {
                    id: filterFacet,
                    name: '',
                    total: 0,
                    values: [],
                };
                return itemResult;
            });
    }, [facetsProps.facets, filterFacetsConfig]);
    useEffect(() => {
        if (!data.length || !cqType || isTracked.current || !categoryIds.length || !currencyRate) {
            return;
        }
        isTracked.current = true;
        setDigitalData(DigitalDataMutation.PLP_BLOOMREACH_VIEW, {
            products: data,
            cursor: {
                from: isEmpty(data) ? 0 : 1,
                till: size(data),
                total,
            },
            pdpPageUrl: pdpPageUrl ?? '',
            cqType,
            sortBy: sortOrderOption.value,
            category: categoryIds[0],
        }, currencyRate);
    }, [categoryIds, cqType, currencyRate, data, pdpPageUrl, sortOrderOption.value, total]);
    const counterLabel = isEmpty(data) ? '' : `1-${data.length} ${t('filters_of_label')} ${total}`;
    const setInputValueHandler = useCallback((opt) => {
        searchInput.handleSearch(opt);
    }, [searchInput]);
    const handleOpenMobileFilters = useCallback(() => {
        setIsFiltersBottomSheetOpened(true);
    }, []);
    const handleCloseMobileFilters = useCallback(() => {
        delay(() => setIsFiltersBottomSheetOpened(false), 100);
    }, []);
    const showDiscountCheckbox = hasDiscountedProducts && !discountedApi.isDiscountFromAEM;
    // Common "SortOrder" Component (for all dimensions/screens)
    const SortOrderComponent = (React.createElement(SortOrderStyled, null,
        React.createElement(Dropdown, { id: "sort_order", title: t('filters_sortBy_label'), isInheritBackground: true, useForMobile: true, hasRadioButton: true, leftSideIconType: isInfiniteDesktopView ? SortFull : SortHalf, isRightToLeft: isRightToLeft, items: sortingProps.sortFilterOptions, activeItem: sortOrderOption.value, onChange: sortingProps.handleSortOrderChange, dataTestId: "sorting" })));
    // Common "ClearAll" Component
    const ClearAllComponent = (React.createElement(ClearAll, { label: t('filters_clearFilters_label'), isRightToLeft: isRightToLeft, onClick: filterClearedProps.handleFilterCleared, isViewShort: true }));
    // Common "SearchInput" Component (for all dimensions/screens)
    const SearchComponent = (React.createElement(SearchInputStyled, { placeholder: t('productsGrid_searchByName_placeholder'), inputValue: autoCorrectQueryResult || searchInput.searchInputValue, setInputValue: setInputValueHandler, minSearchSymbols: MIN_SEARCH_SYMBOLS, maxSearchSymbols: MAX_SEARCH_SYMBOLS, isLabelVisible: false }));
    // "PillsPanel" Component (only for desktop views/screens)
    const PillsPanel = (React.createElement(FlexWrapper, { isFullWidth: true, alignItems: "flex-start", gap: "24px", justifyContent: "space-between" },
        React.createElement(FlexWrapper, { flexShrink: "1" },
            React.createElement(Pills, { singlePill: pills.singlePill, multiplePills: pills.multiplePills, isRightToLeft: isRightToLeft, dictionary: dictionary })),
        React.createElement(FlexWrapper, { flexShrink: "0" }, ClearAllComponent)));
    // Common "ResultsComponent" Component (for all views/screens)
    const ResultsComponent = (React.createElement(FlexWrapper, { mb: "56px", position: "relative" },
        React.createElement(FilterResults, { productEndpoint: productEndpoint, productType: productType, pdpPageUrl: pdpPageUrl, isLoaded: isLoaded, isInEditor: isInEditor, productListData: data, perPageLimitSetting: getDefaultPerPageLimit(window.innerWidth, perPageLimitSetting), updateItemsRef: updateItemsRef, isDataExists: isDataExists, isRightToLeft: isRightToLeft, daysToShowNew: daysToShowNew, onScrollReachEnd: fetchMoreProducts, categoryIds: categoryIds }),
        isLoading && (React.createElement(SpinnerLoaderContainerStyled, null,
            React.createElement(SpinnerLoader, { isLoading: isLoading, hasBackground: false })))));
    // Common "FiltersButton" Component (only for mobile view/screens)
    const FiltersButton = (React.createElement(FiltersButtonStyled, { iconType: FilterSettings, isFirstIcon: true, onClick: handleOpenMobileFilters }, filtersLabel));
    // Common Filters Component for Mobile/TabletDesktop views
    const CommonFiltersComponents = useMemo(() => {
        return facets
            .filter((item) => item.values.length)
            .map((item, index) => {
            const itemValues = item.values;
            const facetGroupId = item.id;
            const filterDropdownOptions = getOptionsForFilter({
                values: itemValues,
                t,
                facetId: facetGroupId,
                additionalText: t('productsGrid_includesSpaceForLock_hint'),
            });
            const filterType = FilterType.CHECKLIST;
            const key = isInfiniteDesktopView ? `desktop_view_${index}` : `mobile_view_${index}`;
            return (React.createElement(FilterDropdown, { key: key, options: filterDropdownOptions, titleLabel: item.name, emptyPlaceholderLabel: t(getPlaceholderLabelKey(filterType)), selectedPlaceholderLabel: t('filters_optionsSelected_placeholder'), isRightToLeft: isRightToLeft, type: filterType, isListItemView: !isInfiniteDesktopView, isCustomVerticalView: isInfiniteDesktopView, countAppliedValues: getCountAppliedValues(itemValues || []) },
                React.createElement(CheckboxesDropdown, { isCheckboxesViewSimple: true, labels: {
                        filters_selectAll_label: t('filters_selectAll_label'),
                        filters_clearAll_label: t('filters_clearAll_label'),
                        common_confirm_button: t('common_confirm_button'),
                    }, onSelect: facetsProps.handleUpdateFacets(facetGroupId), isRightToLeft: isRightToLeft, hasAdditionalControl: isInfiniteDesktopView, isCounterAlwaysVisible: true, isZeroItemsVisible: true })));
        });
    }, [facets, t, isInfiniteDesktopView, isRightToLeft, facetsProps]);
    const Filters = (React.createElement(React.Fragment, null,
        showDiscountCheckbox && (React.createElement(FlexWrapperStyled, { bg: colorCoreWhite1000, padding: "16px", as: isInfiniteDesktopView ? 'div' : 'li', justifyContent: "space-between", isBorderShown: !isInfiniteDesktopView },
            React.createElement(RegularTextLevel3, { isUserSelectDisabled: true }, t('productsGrid_discounted_toggle')),
            React.createElement(ToggleComponent, { isRightToLeft: isRightToLeft, onClickHandler: discountedApi.onSetDiscountHandler, isActiveElement: discountedApi.isShowOnlyDiscounted }))),
        CommonFiltersComponents));
    // Only for mobile view
    const MobileViewComponent = (React.createElement(React.Fragment, null,
        React.createElement(Row, null,
            React.createElement(Col, null,
                React.createElement(FlexWrapper, { mb: "12px", flexDirection: "column" }, SearchComponent)),
            React.createElement(Col, null,
                React.createElement(FlexWrapper, { gap: "12px" },
                    React.createElement(FlexWrapper, { flexShrink: "0" }, FiltersButton),
                    React.createElement(FlexWrapper, { overflow: "hidden", isFullWidth: true }, SortOrderComponent)))),
        React.createElement(Row, null,
            React.createElement(Col, null,
                React.createElement(FlexWrapper, { mt: "12px", flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
                    counterLabel && (React.createElement(FlexWrapper, { flex: "0 1 auto" },
                        React.createElement(ResultItemsCounterStyled, null, counterLabel))),
                    !filterClearedProps.isFilterCleared && ClearAllComponent))),
        React.createElement(Row, null,
            React.createElement(Col, null,
                React.createElement(FlexWrapper, { mt: "12px", flexDirection: "column" },
                    autoCorrectQueryResult && (React.createElement(DidYouMeanForGrid, { query: searchInput.searchInputValue, autoCorrectQueryResult: autoCorrectQueryResult, didYouMeanOptions: didYouMeanOptions, searchResultsPageUrl: pathname })),
                    ResultsComponent)))));
    // Only for tablet view
    const TabletViewComponent = (React.createElement(React.Fragment, null,
        React.createElement(Row, null,
            React.createElement(Col, null,
                React.createElement(FlexWrapper, { gap: "24px" },
                    counterLabel && (React.createElement(FlexWrapper, { flex: "0 1 auto" },
                        React.createElement(ResultItemsCounterStyled, null, counterLabel))),
                    React.createElement(FlexWrapper, { gap: "16px", flex: "1 1 auto" },
                        React.createElement(FlexWrapper, { flex: "1 1 auto" }, SearchComponent),
                        React.createElement(FlexWrapper, { flex: "0 1 auto" }, FiltersButton),
                        React.createElement(FlexWrapper, { flex: "0 1 auto" }, SortOrderComponent))))),
        !filterClearedProps.isFilterCleared && (React.createElement(Row, { mt: "24px" },
            React.createElement(Col, null,
                React.createElement(FlexWrapper, { flexDirection: "row", alignItems: "center", justifyContent: "flex-end" }, ClearAllComponent)))),
        React.createElement(Row, { mt: "24px" },
            autoCorrectQueryResult && (React.createElement(Col, null,
                React.createElement(DidYouMeanForGrid, { query: searchInput.searchInputValue, autoCorrectQueryResult: autoCorrectQueryResult, didYouMeanOptions: didYouMeanOptions, searchResultsPageUrl: pathname }))),
            React.createElement(Col, null, ResultsComponent))));
    const DesktopViewComponent = (React.createElement(React.Fragment, null,
        React.createElement(RowStyled, null,
            React.createElement(Col, { lg: 3 },
                React.createElement(FlexWrapper, { alignItems: "center", height: "100%" },
                    React.createElement(HeadingLevel3, null, t('productsGrid_filtersAndRefine_header')))),
            React.createElement(Col, { lg: 9 },
                React.createElement(FlexWrapper, { gap: "12px" },
                    counterLabel && (React.createElement(FlexWrapper, { flex: "0 1 auto" },
                        React.createElement(ResultItemsCounterStyled, null, counterLabel))),
                    React.createElement(FlexWrapper, { flex: "1 1 auto" }, SearchComponent),
                    React.createElement(FlexWrapper, { flex: "0 1 auto" }, SortOrderComponent)))),
        React.createElement(Row, null,
            React.createElement(Col, { lg: 3 },
                React.createElement(FilterWrapperStyled, { flexDirection: "column" },
                    showDiscountCheckbox && (React.createElement(FlexWrapper, { bg: colorCoreWhite1000, padding: "16px", justifyContent: "space-between" },
                        React.createElement(RegularTextLevel3, { isUserSelectDisabled: true }, t('productsGrid_discounted_toggle')),
                        React.createElement(ToggleComponent, { isRightToLeft: isRightToLeft, onClickHandler: discountedApi.onSetDiscountHandler, isActiveElement: discountedApi.isShowOnlyDiscounted }))),
                    CommonFiltersComponents)),
            React.createElement(Col, { lg: 9 },
                pills.isPillsExist && React.createElement(FlexWrapper, { mb: "24px" }, PillsPanel),
                autoCorrectQueryResult && (React.createElement(DidYouMeanForGrid, { query: searchInput.searchInputValue, autoCorrectQueryResult: autoCorrectQueryResult, didYouMeanOptions: didYouMeanOptions, searchResultsPageUrl: pathname })),
                ResultsComponent))));
    return (React.createElement(React.Fragment, null,
        React.createElement(Container, null,
            React.createElement(SectionWrapper, { isRightToLeft: isRightToLeft },
                React.createElement(FlexWrapper, { flexDirection: "column" },
                    React.createElement(FlexWrapper, { mb: "56px" },
                        React.createElement(Container, { expanded: true, width: "100%", isLoading: isLoading, id: containerId },
                            isMobileView && MobileViewComponent,
                            isTabletView && TabletViewComponent,
                            isInfiniteDesktopView && DesktopViewComponent))))),
        React.createElement(BottomSheet, { initialFocusRef: false, open: isFiltersBottomSheetOpened, header: React.createElement(Header, { headerLeft: React.createElement(RegularTextLevel2, { weight: 500 }, filtersLabel), headerRight: !filterClearedProps.isFilterCleared ? (React.createElement(RegularTextLevel3, { as: "span", onClick: filterClearedProps.handleFilterCleared }, t('filters_clearFilters_label'))) : undefined }), footer: React.createElement(Button, { size: SIZE_44, hasIconButton: NO_ICON, buttonVariant: CONTAINED_VARIANT, label: !filterClearedProps.isFilterCleared
                    ? `${t('common_showResults_button')} (${total})`
                    : t('common_showResults_button'), onClick: handleCloseMobileFilters }), onDismiss: handleCloseMobileFilters },
            React.createElement(FiltersListStyled, null, Filters))));
}
AccessoriesFilterBloomreach.displayName = 'AccessoriesFilter_Organism';
